import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ChevronDown, X, Anchor, Calendar, MapPin, Ship } from "lucide-react";
import { setSelectedVoyage } from "../../store/voyageFilterSlice";
import { getVesselVoyagesByVoyageId } from "../../services/vesselService";
import { format } from "date-fns";

const safeFormatDate = (date) => {
  if (!date) return "N/A";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    console.warn("Invalid date value:", date);
    return "N/A";
  }
  try {
    return format(parsedDate, "MMM dd, yyyy");
  } catch (error) {
    console.error("Date formatting failed:", error);
    return "N/A";
  }
};

const VoyageFilter = ({ autoSelectLatest = false }) => {
  const dispatch = useDispatch();
  const { selectedVoyage } = useSelector((state) => state.voyageFilter);
  const currentVessel = useSelector(
    (state) => state.vesselOverview.currentVessel
  );
  const [voyages, setVoyages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [userCleared, setUserCleared] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchVoyages = async () => {
      const vesselId = currentVessel?._id || currentVessel?.id;
      if (!vesselId) {
        console.warn("No vesselId found for current vessel!");
        setVoyages([]);
        return;
      }
      setLoading(true);
      try {
        const response = await getVesselVoyagesByVoyageId(vesselId);
        const voyagesData = response?.data?.data || [];
        console.log("voyagesData: ", voyagesData);

        // Sort voyages by createdAt in descending order to get the latest first
        const sortedVoyages = voyagesData.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.departTime || 0);
          const dateB = new Date(b.createdAt || b.departTime || 0);
          return dateB - dateA;
        });

        setVoyages(sortedVoyages);

        // Auto-select the latest voyage ONLY if autoSelectLatest is true
        if (autoSelectLatest && !hasAutoSelected && !userCleared) {
          if (sortedVoyages.length > 0) {
            const latestVoyage = sortedVoyages[0];
            console.log("Auto-selecting latest voyage:", latestVoyage);
            dispatch(setSelectedVoyage(latestVoyage));
            setHasAutoSelected(true);
          } else {
            setUserCleared(true);
            dispatch(setSelectedVoyage(null));
          }
        }
      } catch (error) {
        console.error("Failed to fetch voyages:", error);
        setVoyages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVoyages();
  }, [currentVessel, dispatch, hasAutoSelected, userCleared, autoSelectLatest]);

  // Reset auto-selection when vessel changes
  useEffect(() => {
    setHasAutoSelected(false);
    setUserCleared(false);
  }, [currentVessel?._id]);

  const handleVoyageSelect = (voyage) => {
    dispatch(setSelectedVoyage(voyage));
    setShowDropdown(false);
    setHasAutoSelected(true);
    setUserCleared(false);
  };

  const handleClearSelection = () => {
    dispatch(setSelectedVoyage(null));
    setUserCleared(true);
  };

  const formatVoyageDisplay = (voyage) => {
    if (!voyage) return "Select Voyage";
    return `${voyage.voyageId}`;
  };

  return (
    <div className="relative mr-2" ref={dropdownRef}>
      <div className="flex items-center gap-2">
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
            className="flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-xs min-w-[280px] max-w-[300px]"
            disabled={loading}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Ship className="w-4 h-4 text-blue-500 flex-shrink-0" />
              {loading ? (
                <span className="text-gray-500 truncate">
                  Loading voyages...
                </span>
              ) : selectedVoyage ? (
                <div className="flex items-center gap-2 truncate">
                  <span className="truncate">
                    {formatVoyageDisplay(selectedVoyage)}
                  </span>
                </div>
              ) : (
                <span className="text-gray-800 truncate">Select Voyage</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {selectedVoyage && (
                <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full whitespace-nowrap">
                  {`${selectedVoyage.fromPort
                    ?.slice(0, 3)
                    .toUpperCase()} - ${selectedVoyage.toPort
                    ?.slice(0, 3)
                    .toUpperCase()}`}
                </div>
              )}

              {selectedVoyage && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSelection();
                  }}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  title="Clear voyage selection"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              <ChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${
                  showDropdown ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {showDropdown && (
        <div className="absolute z-50 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-72 overflow-y-auto min-w-[280px]">
          <div className="p-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Anchor className="w-4 h-4" />
              Available Voyages
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {autoSelectLatest
                ? userCleared
                  ? "Selection cleared - choose a voyage"
                  : "Latest voyage auto-selected"
                : "Select a voyage to filter data"}
            </p>
          </div>

          {voyages.length === 0 ? (
            <div className="p-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <Ship className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 font-medium">
                {loading ? "Loading voyages..." : "No voyages found"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {loading ? "" : "This vessel has no voyage records"}
              </p>
            </div>
          ) : (
            <div className="py-2">
              {voyages.map((voyage, index) => (
                <button
                  key={voyage._id}
                  onClick={() => handleVoyageSelect(voyage)}
                  className={`w-full px-4 py-3 text-left transition-all hover:bg-blue-50 ${
                    selectedVoyage?._id === voyage._id
                      ? "bg-blue-50 border-l-4 border-l-blue-500"
                      : "border-l-4 border-l-transparent"
                  } ${index === 0 ? "border-l-4 border-l-green-500" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-gray-900">
                      {voyage.voyageId}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    {voyage.fromPort} → {voyage.toPort}
                  </div>
                  {voyage.createdAt && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                      <Calendar className="w-3 h-3" />
                      <span>Created: {safeFormatDate(voyage.createdAt)}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoyageFilter;
